import {
  ApiError,
  TaskResponse,
  TaskStatus,
  TasksService,
  Auth0AccountRole,
  TaskRecipientCreate,
  TaskRecipientDelete,
  StudentLimitedResponse,
  ParentLimitedResponse,
  AccountLimitedResponse,
  OrganizationsService,
  TutorPublicResponse,
  OrganizationPublicResponse,
  TaskRecipientResponse,
} from "client/openapi";
import { Dialog, DialogContent, DialogTrigger } from "components/Dialog";
import {
  forwardRef,
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Notifications from "util/notifications";
import "./index.css";
import { Select } from "components/Select";
import { DatePicker } from "rsuite";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { ChevronDownIcon, Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import moment from "moment";
import { createPortal } from "react-dom";
import { dateInputClasses } from "../UpcomingMeetings/Components/dateRangeSelector";
import PersonPill, { PillColor } from "components/PersonPill";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import DeleteTaskDialog from "./delete";
import CreateTaskDialog from "./create";
import { announceTasksChanged, TASKS_CHANGED } from "./tasksChanged";
import { PersonKind } from "client/openapi/models/PersonKind";
import {
  byName,
  fullName,
  recipientPerson,
  TaskPerson,
  TaskRole,
  taskPeople,
} from "./PeoplePicker";
import TaskFields, { TaskDraft } from "./TaskFields";
import {
  AutomaticChip,
  automationOfTask,
  NextUp,
  TasksWaiting,
  useTaskAutomations,
} from "components/Automations/TaskAutomations";
import type { ApprovalItem } from "components/Automations/types";

const getIdChanges = (
  originalIds: (string | number)[],
  updatedIds: (string | number)[],
  role: Auth0AccountRole,
  findAddedIds: boolean
) => {
  let addedIds: (string | number)[] = [];
  let removedIds: (string | number)[] = [];

  if (findAddedIds) {
    addedIds = updatedIds.filter((id) => !originalIds.includes(id));
  } else {
    removedIds = originalIds.filter((id) => !updatedIds.includes(id));
  }

  const ids = findAddedIds ? addedIds : removedIds;

  if (role === Auth0AccountRole.ORG_ADMIN) {
    return ids.map((id) => ({
      account_id: String(id),
      student_id: null,
      parent_id: null,
      tutor_id: null,
    }));
  } else if (role === Auth0AccountRole.ORG_TUTOR) {
    return ids.map((id) => ({
      account_id: null,
      student_id: null,
      parent_id: null,
      tutor_id: Number(id),
    }));
  } else if (role === Auth0AccountRole.PARENT) {
    return ids.map((id) => ({
      account_id: null,
      student_id: null,
      parent_id: Number(id),
      tutor_id: null,
    }));
  } else if (role === Auth0AccountRole.ME) {
    return ids.map((id) => ({
      account_id: null,
      student_id: Number(id),
      parent_id: null,
      tutor_id: null,
    }));
  }
  return [];
};

/** The views' names, in the form's words: who a task is assigned to, or about. */
const VIEW_LABELS: Record<TaskFilters["view"], string> = {
  all_tasks: "All Tasks",
  my_tasks: "Assigned to me",
  assigned_to_admins: "Assigned to an admin",
  assigned_to_tutors: "Assigned to a tutor",
  assigned_to_parents: "Assigned to a parent",
  assigned_to_students: "Assigned to a student",
  tagged_to_admins: "About an admin",
  tagged_to_tutors: "About a tutor",
  tagged_to_parents: "About a parent",
  tagged_to_students: "About a student",
};

const ADMIN_VIEWS: TaskFilters["view"][] = [
  "all_tasks",
  "my_tasks",
  "assigned_to_admins",
  "assigned_to_tutors",
  "assigned_to_parents",
  "assigned_to_students",
  "tagged_to_admins",
  "tagged_to_tutors",
  "tagged_to_parents",
  "tagged_to_students",
];

const TUTOR_VIEWS: TaskFilters["view"][] = [
  "my_tasks",
  "assigned_to_students",
  "tagged_to_students",
];

interface TaskFilters {
  search: string;
  sortBy: "due_date_asc" | "due_date_desc";
  dateRange: {
    start: string | null;
    end: string | null;
  };
  status: TaskStatus | "ALL";
  view:
    | "all_tasks"
    | "my_tasks"
    | "assigned_to_admins"
    | "assigned_to_tutors"
    | "assigned_to_parents"
    | "assigned_to_students"
    | "tagged_to_admins"
    | "tagged_to_parents"
    | "tagged_to_students"
    | "tagged_to_tutors";
  selectedUsers: { value: number | string; label: string }[];
}

interface UserOption {
  value: number | string;
  label: string;
}

/** A task's edits: the form's fields, or just a new status. */
interface TaskUpdateHelper extends Partial<TaskDraft> {
  status?: TaskStatus;
}

/**
 * Where the dashboard's Tasks view puts its controls, so they sit where the
 * card's other views put theirs: the date range in the header row, and the
 * dropdowns with Create Task at their right in the row under it.
 */
export type TasksViewSlots = {
  dates?: HTMLElement | null;
  filters?: HTMLElement | null;
};

/** The dialog, or the same content in place (the dashboard's Tasks view). */
const TasksFrame = forwardRef<
  HTMLDivElement,
  { embedded: boolean; children: ReactNode }
>(({ embedded, children }, ref) =>
  embedded ? (
    <div ref={ref} className="manage-tasks-view">
      {children}
    </div>
  ) : (
    <DialogContent ref={ref} className="manage-tasks-dialog">
      {children}
    </DialogContent>
  )
);

function ManageTasksDialog({
  startingView = "my_tasks",
  startingUserId,
  startingUserRole,
  startingUserName,
  startingStatus = "ALL",
  embedded = false,
  slots = {},
}: {
  startingView?: TaskFilters["view"];
  startingStatus?: TaskFilters["status"];
  startingUserId?: number | string;
  startingUserRole?: Auth0AccountRole;
  startingUserName?: string;
  /** Drawn in place, without the dialog (the dashboard's Tasks view). */
  embedded?: boolean;
  /** With embedded: where its controls go. */
  slots?: TasksViewSlots;
}) {
  const { account, currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  const [organization, setOrganization] =
    useState<OrganizationPublicResponse>();

  const [allTasks, setAllTasks] = useState<TaskResponse[]>([]);
  // The first load has finished (the Tasks view shows "Loading..." until then).
  const [loaded, setLoaded] = useState(false);
  const [myTasks, setMyTasks] = useState<Set<number>>(new Set());

  const [adminUsers, setAdminUsers] = useState<UserOption[]>([]);
  const [tutorUsers, setTutorUsers] = useState<UserOption[]>([]);
  const [studentUsers, setStudentUsers] = useState<UserOption[]>([]);
  const [parentUsers, setParentUsers] = useState<UserOption[]>([]);

  const [taskUpdates, setTaskUpdates] = useState<
    Record<number, TaskUpdateHelper>
  >({});
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());

  const [filters, setFilters] = useState<TaskFilters>({
    search: "",
    sortBy: "due_date_asc",
    dateRange: {
      start: null,
      end: null,
    },
    status: startingStatus,
    view: startingView,
    selectedUsers: [],
  });
  const [error, setError] = useState<ApiError>();
  // Automations: what pops up after a status change.
  const automations = useTaskAutomations();
  const [nextUp, setNextUp] = useState<ApprovalItem[]>([]);
  const popUp = (items: ApprovalItem[]) => items.length && setNextUp(items);

  const dialogRef = useRef<HTMLDivElement>(null);

  // Add refreshTrigger state
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Create a refresh function
  const refreshTasks = () => setRefreshTrigger((prev) => prev + 1);

  // Tell the page when tasks change here, and reload when they change
  // anywhere else (another dialog, the People panel's add task).
  const announcing = useRef(false);
  const tasksChanged = () => {
    announcing.current = true;
    announceTasksChanged();
    announcing.current = false;
  };
  const tasksCreated = () => {
    refreshTasks();
    tasksChanged();
  };
  useEffect(() => {
    const onChange = () => {
      if (!announcing.current) setRefreshTrigger((prev) => prev + 1);
    };
    window.addEventListener(TASKS_CHANGED, onChange);
    return () => window.removeEventListener(TASKS_CHANGED, onChange);
  }, []);

  // Pop-ups stay inside the dialog, so it keeps focus; in place they open
  // on the page, so the card's scrolling doesn't clip them.
  const popupContainer = () =>
    (!embedded && dialogRef.current) || document.body;

  // Add a new state to track which tasks are being updated
  const [updatingTasks, setUpdatingTasks] = useState<Set<number>>(new Set());

  // Fetch organization data once when component mounts
  useEffect(() => {
    if (currently_selected_organization && currently_selected_role) {
      Promise.all([
        // get tasks assigned to all admins, tutors, parents, and students
        // linked to the current account
        TasksService.getTasksByAccount(),
        // get tasks organization wide if on the admin dashboard
        currently_selected_role === Auth0AccountRole.ORG_ADMIN
          ? TasksService.getOrgTasks({
              orgId: currently_selected_organization,
            })
          : Promise.resolve([]),
        // get tasks for all students of tutors or parents
        // linked to the current account
        currently_selected_role === Auth0AccountRole.ORG_TUTOR ||
        currently_selected_role === Auth0AccountRole.PARENT
          ? TasksService.getTasksForStudentsOfAccount()
          : Promise.resolve([]),
        // get organization data
        OrganizationsService.getOrganization({
          orgId: currently_selected_organization,
        }),
      ]).then(([accountTasks, orgTasks, studentTasks, organizationResult]) => {
        setMyTasks(new Set(accountTasks.map((t) => t.id)));
        const uniqueTasks = new Map();
        [...accountTasks, ...orgTasks, ...studentTasks].forEach((task) => {
          uniqueTasks.set(task.id, task);
        });
        setAllTasks(Array.from(uniqueTasks.values()));
        setOrganization(organizationResult);
        setLoaded(true);
      });
    }
  }, [
    currently_selected_organization,
    currently_selected_role,
    refreshTrigger,
  ]);

  // The people filter's options, by name (the API lists people in no
  // particular order).
  useEffect(() => {
    if (organization) {
      if ("students" in organization) {
        setStudentUsers(
          (organization.students as StudentLimitedResponse[])
            ?.map((s) => ({ value: s.id, label: fullName(s) }))
            .sort(byName) ?? []
        );
      }

      if ("parents" in organization) {
        setParentUsers(
          (organization.parents as ParentLimitedResponse[])
            ?.map((p) => ({ value: p.id, label: fullName(p) }))
            .sort(byName) ?? []
        );
      }

      if ("tutors" in organization) {
        setTutorUsers(
          (organization.tutors as TutorPublicResponse[])
            ?.map((t) => ({ value: t.id, label: fullName(t) }))
            .sort(byName) ?? []
        );
      }

      if ("admins" in organization) {
        setAdminUsers(
          (organization.admins as AccountLimitedResponse[])
            ?.map((a) => ({ value: a.reference_id, label: fullName(a) }))
            .sort(byName) ?? []
        );
      }
    }
  }, [organization]);

  // Reset selected users when view changes (e.g whether we are showing "My Tasks", "Admin Tasks",
  // "Parent Tasks", "Student Tasks", or "Tutor Tasks"). When selectedUsers is an empty list, all
  // tasks for the selected view will be shown, until a user filter (e.g a specific admins, parents,
  // students, or tutors) are selected.
  useEffect(() => {
    setFilters((prev) => ({ ...prev, selectedUsers: [] }));
  }, [filters.view]);

  const toggleTaskExpansion = (taskId: number) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  useEffect(() => {
    if (!organization) return;

    if (startingUserId && startingUserRole) {
      let matchedUser: UserOption | undefined;

      switch (startingUserRole) {
        case Auth0AccountRole.ORG_ADMIN:
          matchedUser = adminUsers.find((u) => u.value === startingUserId);
          break;
        case Auth0AccountRole.ORG_TUTOR:
          matchedUser = tutorUsers.find((u) => u.value === startingUserId);
          break;
        case Auth0AccountRole.PARENT:
          matchedUser = parentUsers.find((u) => u.value === startingUserId);
          break;
        case Auth0AccountRole.ME:
          // ME role typically means a student account
          matchedUser = studentUsers.find((u) => u.value === startingUserId);
          break;
        default:
          // In case you have other roles
          break;
      }

      // An archived person is left out of the user lists, so their profile
      // passes their name.
      const startingUser =
        matchedUser ??
        (startingUserName
          ? { value: startingUserId, label: startingUserName }
          : undefined);

      if (startingUser) {
        setFilters((prev) => ({
          ...prev,
          selectedUsers: [startingUser],
        }));
      }
    }
  }, [
    organization,
    adminUsers,
    tutorUsers,
    parentUsers,
    studentUsers,
    startingUserId,
    startingUserRole,
    startingUserName,
  ]);

  const handleTaskUpdate = (
    currentTask: TaskResponse,
    taskUpdate: TaskUpdateHelper
  ) => {
    if (!taskUpdate) {
      return;
    }

    let nameChanged = taskUpdate.name && currentTask.name !== taskUpdate.name;
    // The description is optional, so clearing it ("") is a change too.
    const contentChanged =
      taskUpdate.content !== undefined &&
      taskUpdate.content !== currentTask.content;
    let dueDateChanged =
      taskUpdate.due_date && currentTask.due_date !== taskUpdate.due_date;
    let statusChanged =
      taskUpdate.status && currentTask.status !== taskUpdate.status;

    // Initialize original users from current task
    const original_users = {
      assignedAdmins: currentTask.assigned_users
        .filter((u) => u.account)
        .map((u) => u.account!.reference_id),
      assignedTutors: currentTask.assigned_users
        .filter((u) => u.tutor)
        .map((u) => u.tutor!.id),
      assignedStudents: currentTask.assigned_users
        .filter((u) => u.student)
        .map((u) => u.student!.id),
      assignedParents: currentTask.assigned_users
        .filter((u) => u.parent)
        .map((u) => u.parent!.id),
      taggedAdmins: currentTask.tagged_users
        .filter((u) => u.account)
        .map((u) => u.account!.reference_id),
      taggedTutors: currentTask.tagged_users
        .filter((u) => u.tutor)
        .map((u) => u.tutor!.id),
      taggedStudents: currentTask.tagged_users
        .filter((u) => u.student)
        .map((u) => u.student!.id),
      taggedParents: currentTask.tagged_users
        .filter((u) => u.parent)
        .map((u) => u.parent!.id),
    };

    // People not in the edit (a status change) stay as they are.
    const idsFor = (people: TaskPerson[] | undefined, role: TaskRole) =>
      people?.filter((p) => p.role === role).map((p) => p.id);
    const updatedUsers = {
      assignedAdmins:
        (idsFor(taskUpdate.assigned, "admin") as string[] | undefined) ??
        original_users.assignedAdmins,
      assignedTutors:
        (idsFor(taskUpdate.assigned, PersonKind.TUTOR) as
          | number[]
          | undefined) ?? original_users.assignedTutors,
      assignedStudents:
        (idsFor(taskUpdate.assigned, PersonKind.STUDENT) as
          | number[]
          | undefined) ?? original_users.assignedStudents,
      assignedParents:
        (idsFor(taskUpdate.assigned, PersonKind.PARENT) as
          | number[]
          | undefined) ?? original_users.assignedParents,
      taggedAdmins:
        (idsFor(taskUpdate.tagged, "admin") as string[] | undefined) ??
        original_users.taggedAdmins,
      taggedTutors:
        (idsFor(taskUpdate.tagged, PersonKind.TUTOR) as number[] | undefined) ??
        original_users.taggedTutors,
      taggedStudents:
        (idsFor(taskUpdate.tagged, PersonKind.STUDENT) as
          | number[]
          | undefined) ?? original_users.taggedStudents,
      taggedParents:
        (idsFor(taskUpdate.tagged, PersonKind.PARENT) as
          | number[]
          | undefined) ?? original_users.taggedParents,
    };

    const addedAssignedUsers: TaskRecipientCreate[] = [
      ...getIdChanges(
        original_users.assignedAdmins,
        updatedUsers.assignedAdmins,
        Auth0AccountRole.ORG_ADMIN,
        true
      ),
      ...getIdChanges(
        original_users.assignedTutors,
        updatedUsers.assignedTutors,
        Auth0AccountRole.ORG_TUTOR,
        true
      ),
      ...getIdChanges(
        original_users.assignedParents,
        updatedUsers.assignedParents,
        Auth0AccountRole.PARENT,
        true
      ),
      ...getIdChanges(
        original_users.assignedStudents,
        updatedUsers.assignedStudents,
        Auth0AccountRole.ME,
        true
      ),
    ];
    const removedAssignedUsers: TaskRecipientDelete[] = [
      ...getIdChanges(
        original_users.assignedAdmins,
        updatedUsers.assignedAdmins,
        Auth0AccountRole.ORG_ADMIN,
        false
      ),
      ...getIdChanges(
        original_users.assignedTutors,
        updatedUsers.assignedTutors,
        Auth0AccountRole.ORG_TUTOR,
        false
      ),
      ...getIdChanges(
        original_users.assignedParents,
        updatedUsers.assignedParents,
        Auth0AccountRole.PARENT,
        false
      ),
      ...getIdChanges(
        original_users.assignedStudents,
        updatedUsers.assignedStudents,
        Auth0AccountRole.ME,
        false
      ),
    ];
    const addedTaggedUsers: TaskRecipientCreate[] = [
      ...getIdChanges(
        original_users.taggedAdmins,
        updatedUsers.taggedAdmins,
        Auth0AccountRole.ORG_ADMIN,
        true
      ),
      ...getIdChanges(
        original_users.taggedTutors,
        updatedUsers.taggedTutors,
        Auth0AccountRole.ORG_TUTOR,
        true
      ),
      ...getIdChanges(
        original_users.taggedParents,
        updatedUsers.taggedParents,
        Auth0AccountRole.PARENT,
        true
      ),
      ...getIdChanges(
        original_users.taggedStudents,
        updatedUsers.taggedStudents,
        Auth0AccountRole.ME,
        true
      ),
    ];
    const removedTaggedUsers: TaskRecipientDelete[] = [
      ...getIdChanges(
        original_users.taggedAdmins,
        updatedUsers.taggedAdmins,
        Auth0AccountRole.ORG_ADMIN,
        false
      ),
      ...getIdChanges(
        original_users.taggedTutors,
        updatedUsers.taggedTutors,
        Auth0AccountRole.ORG_TUTOR,
        false
      ),
      ...getIdChanges(
        original_users.taggedParents,
        updatedUsers.taggedParents,
        Auth0AccountRole.PARENT,
        false
      ),
      ...getIdChanges(
        original_users.taggedStudents,
        updatedUsers.taggedStudents,
        Auth0AccountRole.ME,
        false
      ),
    ];

    if (
      nameChanged ||
      contentChanged ||
      dueDateChanged ||
      statusChanged ||
      addedAssignedUsers.length > 0 ||
      removedAssignedUsers.length > 0 ||
      addedTaggedUsers.length > 0 ||
      removedTaggedUsers.length > 0
    ) {
      setUpdatingTasks(
        (prev) => new Set(Array.from(prev).concat(currentTask.id))
      );
      TasksService.updateTask({
        taskId: currentTask.id,
        requestBody: {
          name: nameChanged ? taskUpdate.name : undefined,
          content: contentChanged ? taskUpdate.content : undefined,
          due_date: dueDateChanged ? taskUpdate.due_date : undefined,
          status: statusChanged ? taskUpdate.status : undefined,
          add_assigned_users:
            addedAssignedUsers.length > 0 ? addedAssignedUsers : undefined,
          remove_assigned_users:
            removedAssignedUsers.length > 0 ? removedAssignedUsers : undefined,
          add_tagged_users:
            addedTaggedUsers.length > 0 ? addedTaggedUsers : undefined,
          remove_tagged_users:
            removedTaggedUsers.length > 0 ? removedTaggedUsers : undefined,
        },
      })
        .then((updatedTask) => {
          setAllTasks(
            allTasks.map((t) => (t.id === currentTask.id ? updatedTask : t))
          );
          setTaskUpdates((prev) => {
            const newUpdates = { ...prev };
            delete newUpdates[currentTask.id];
            return newUpdates;
          });
          Notifications.success("Task updated successfully");
          tasksChanged();
          if (statusChanged)
            automations.changedInTadpole(currentTask, updatedTask).then(popUp);
        })
        .catch((err) => {
          setError(err as ApiError);
        })
        .finally(() => {
          // Remove task from updating set
          setUpdatingTasks((prev) => {
            const newSet = new Set(prev);
            newSet.delete(currentTask.id);
            return newSet;
          });
        });
    } else {
      // Nothing changed: close the edit.
      setTaskUpdates((prev) => {
        const next = { ...prev };
        delete next[currentTask.id];
        return next;
      });
    }
  };

  const handleTaskDelete = (taskId: number) => {
    TasksService.deleteTask({ taskId })
      .then(() => {
        setAllTasks(allTasks.filter((t) => t.id !== taskId));
        Notifications.success("Task deleted successfully");
        tasksChanged();
      })
      .catch((err) => {
        setError(err as ApiError);
      });
  };

  const handleUserClick = (userType: string, userId: number) => {
    if (userType === "admin") return; // Don't navigate for admin users
    window.open(`/manage/${userType}s/${userId}`, "_blank");
  };

  const filteredAndSortedTasks = allTasks
    .filter((task) => {
      // search by keyword
      const matchesSearch =
        filters.search === "" ||
        task.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.content.toLowerCase().includes(filters.search.toLowerCase());

      const taskDate = new Date(task.due_date);

      // search by date range
      const matchesDateRange =
        (!filters.dateRange.start ||
          taskDate >= new Date(filters.dateRange.start)) &&
        (!filters.dateRange.end || taskDate <= new Date(filters.dateRange.end));

      // search by status
      const matchesStatus =
        filters.status === "ALL" || task.status === filters.status;

      // Filter based on view and selected users
      let matchesView = true;
      if (
        currently_selected_role &&
        [Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
          currently_selected_role
        )
      ) {
        switch (filters.view) {
          case "all_tasks":
            matchesView = task.org_id === currently_selected_organization;
            break;
          case "my_tasks":
            matchesView = myTasks.has(task.id);
            break;
          case "assigned_to_admins":
            matchesView = task.assigned_users.some((u) => u.account !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.assigned_users.some(
                  (u) =>
                    u.account && selectedUserIds.has(u.account.reference_id)
                );
            }
            break;
          case "tagged_to_admins":
            matchesView = task.tagged_users.some((u) => u.account !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.tagged_users.some(
                  (u) =>
                    u.account && selectedUserIds.has(u.account.reference_id)
                );
            }
            break;
          case "assigned_to_parents":
            matchesView = task.assigned_users.some((u) => u.parent !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.assigned_users.some(
                  (u) => u.parent && selectedUserIds.has(u.parent.id)
                );
            }
            break;
          case "tagged_to_parents":
            matchesView = task.tagged_users.some((u) => u.parent !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.tagged_users.some(
                  (u) => u.parent && selectedUserIds.has(u.parent.id)
                );
            }
            break;
          case "assigned_to_students":
            matchesView = task.assigned_users.some((u) => u.student !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.assigned_users.some(
                  (u) => u.student && selectedUserIds.has(u.student.id)
                );
            }
            break;
          case "tagged_to_students":
            matchesView = task.tagged_users.some((u) => u.student !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.tagged_users.some(
                  (u) => u.student && selectedUserIds.has(u.student.id)
                );
            }
            break;
          case "assigned_to_tutors":
            matchesView = task.assigned_users.some((u) => u.tutor !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.assigned_users.some(
                  (u) => u.tutor && selectedUserIds.has(u.tutor.id)
                );
            }
            break;
          case "tagged_to_tutors":
            matchesView = task.tagged_users.some((u) => u.tutor !== null);
            if (filters.selectedUsers.length > 0) {
              const selectedUserIds = new Set(
                filters.selectedUsers.map((u) => u.value)
              );
              matchesView =
                matchesView &&
                task.tagged_users.some(
                  (u) => u.tutor && selectedUserIds.has(u.tutor.id)
                );
            }
            break;
        }
      }

      return matchesSearch && matchesDateRange && matchesStatus && matchesView;
    })
    .sort((a, b) => {
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return filters.sortBy === "due_date_asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });

  /** People on a task as PersonPills: admins gray, then by role color. */
  const pillPeople = (users: TaskRecipientResponse[]) =>
    users.flatMap((u) => {
      const [person, type, color] = u.account
        ? [u.account, "admin", "gray"]
        : u.tutor
        ? [u.tutor, "tutor", "gold"]
        : u.parent
        ? [u.parent, "parent", "blue"]
        : u.student
        ? [u.student, "student", "green"]
        : [];
      if (!person) return [];
      return [
        {
          key: u.id,
          name: `${person.first_name} ${person.last_name}`,
          color: color as PillColor,
          open:
            type === "admin" || !("id" in person)
              ? undefined
              : () => handleUserClick(type as string, person.id),
        },
      ];
    });

  const renderDescription = (task: TaskResponse) => (
    <>
      <h4 className="text-sm font-bold">Description</h4>
      {/* On faint blue, so it reads apart from the row (Andrew). */}
      <div className="mt-1 rounded-lg bg-blue-50 px-3 py-2">
        <p className="whitespace-pre-wrap">
          {task.content || (
            <span className="text-sm text-neutral-400">None</span>
          )}
        </p>
      </div>
    </>
  );

  const renderUserList = (
    type: "Assigned Users" | "Tagged Users",
    task: TaskResponse
  ) => {
    const people = pillPeople(
      type === "Assigned Users" ? task.assigned_users : task.tagged_users
    );

    return (
      <div className="user-list mb-2">
        {type === "Assigned Users" && (
          <div className="mb-4">{renderDescription(task)}</div>
        )}
        <h4 className="text-sm font-bold">
          {type === "Assigned Users" ? "Assigned to" : "About"}
        </h4>
        <div className="mt-1 flex flex-wrap gap-1">
          {people.map((p) => (
            <PersonPill
              key={p.key}
              label={p.name}
              color={p.color}
              onClick={p.open}
            />
          ))}
          {people.length === 0 && (
            <span className="text-sm text-neutral-400">No one</span>
          )}
        </div>
      </div>
    );
  };

  const viewLabel = (view: TaskFilters["view"]) => VIEW_LABELS[view];

  const viewOptions = (
    currently_selected_role === Auth0AccountRole.ORG_ADMIN
      ? ADMIN_VIEWS
      : currently_selected_role === Auth0AccountRole.ORG_TUTOR
      ? TUTOR_VIEWS
      : []
  ).map((view) => ({ value: view, label: VIEW_LABELS[view] }));

  // The people filter says what the view means: who it's assigned to, or
  // who it's about.
  const userFilterLabel = filters.view.startsWith("tagged_")
    ? "About"
    : "Assigned to";

  const userFilterOptions = filters.view.includes("admins")
    ? adminUsers
    : filters.view.includes("parents")
    ? parentUsers
    : filters.view.includes("students")
    ? studentUsers
    : filters.view.includes("tutors")
    ? tutorUsers
    : [];

  const sortOptions = [
    { value: "due_date_asc", label: "Earlier" },
    { value: "due_date_desc", label: "Later" },
  ];

  const statusFilterOptions = [
    { value: "ALL", label: "All" },
    ...Object.values(TaskStatus).map((status) => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
    })),
  ];

  const startEditing = (task: TaskResponse) =>
    setTaskUpdates((prev) => ({
      ...prev,
      [task.id]: {
        name: task.name,
        content: task.content,
        due_date: task.due_date,
        assigned: task.assigned_users.flatMap(recipientPerson),
        tagged: task.tagged_users.flatMap(recipientPerson),
      },
    }));

  const cancelEditing = (taskId: number) =>
    setTaskUpdates((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });

  // Everyone a task can name, and the signed-in admin, for the edit's pickers.
  const people = taskPeople({
    admins:
      organization && "admins" in organization
        ? (organization.admins as AccountLimitedResponse[])
        : [],
    tutors:
      organization && "tutors" in organization
        ? (organization.tutors as TutorPublicResponse[])
        : [],
    parents:
      organization && "parents" in organization
        ? (organization.parents as ParentLimitedResponse[])
        : [],
    students:
      organization && "students" in organization
        ? (organization.students as StudentLimitedResponse[])
        : [],
  });
  const me: TaskPerson[] = account
    ? [
        {
          role: "admin",
          id: account.reference_id,
          label: `${account.first_name} ${account.last_name}`,
        },
      ]
    : [];

  /** The New task form, filled with the task, with Cancel and Save. */
  const renderEditForm = (task: TaskResponse) => {
    const draft = taskUpdates[task.id] as TaskDraft;
    const onTask = [...task.assigned_users, ...task.tagged_users].flatMap(
      recipientPerson
    );
    const save = () => {
      if (!draft.name.trim() || draft.assigned.length === 0) {
        Notifications.error(
          "Add a task name, a due date and at least one person to assign it to."
        );
        return;
      }
      handleTaskUpdate(task, draft);
    };
    return (
      <div className="task-form max-w-xl">
        <TaskFields
          idPrefix={`task-${task.id}`}
          task={draft}
          onChange={(next) =>
            setTaskUpdates((prev) => ({ ...prev, [task.id]: next }))
          }
          people={people}
          suggestedAssign={[...me, ...onTask]}
          suggestedAbout={[...onTask, ...me]}
          dateContainer={popupContainer}
        />
        <div className="task-form__actions">
          <Button
            size={ButtonSize.SMALL}
            fill={ButtonFill.HOLLOW}
            color={ButtonColor.GREEN}
            onClick={() => cancelEditing(task.id)}
          >
            Cancel
          </Button>
          <Button
            size={ButtonSize.SMALL}
            color={ButtonColor.GREEN}
            onClick={save}
            disabled={updatingTasks.has(task.id)}
          >
            {updatingTasks.has(task.id) ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    );
  };

  const renderStatusSelect = (task: TaskResponse, width = "200px") => (
    <Select
      value={{
        value: task.status,
        label:
          task.status.charAt(0).toUpperCase() +
          task.status.slice(1).toLowerCase(),
      }}
      onChange={(option) => {
        handleTaskUpdate(task, {
          status: option.value as TaskStatus,
        } as TaskUpdateHelper);
      }}
      options={Object.values(TaskStatus).map((status) => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
      }))}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (base) => ({
          ...base,
          width,
        }),
        menu: (base) => ({
          ...base,
          zIndex: 9999,
          pointerEvents: "auto",
        }),
        option: (base) => ({
          ...base,
          cursor: "pointer",
        }),
      }}
      // Above everything, and opening upward when there's no room below.
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="auto"
    />
  );

  /** A task's edit and delete buttons. */
  const renderEditButtons = (task: TaskResponse) => (
    <>
      <Button
        color={ButtonColor.GREEN}
        fill={ButtonFill.HOLLOW}
        size={ButtonSize.SMALL}
        onClick={() => startEditing(task)}
        aria-label="Edit task"
      >
        <Pencil1Icon /> Edit
      </Button>
      <Dialog>
        <DialogTrigger asChild>
          <Button
            color={ButtonColor.RED}
            fill={ButtonFill.HOLLOW}
            size={ButtonSize.SMALL}
            aria-label="Delete task"
          >
            <TrashIcon /> Delete
          </Button>
        </DialogTrigger>

        <DeleteTaskDialog
          task={task}
          onConfirm={() => handleTaskDelete(task.id)}
        />
      </Dialog>
    </>
  );

  const renderCreateTask = (trigger: ReactNode) => (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <CreateTaskDialog
        students={
          organization && "students" in organization
            ? (organization.students as StudentLimitedResponse[])
            : []
        }
        parents={
          organization && "parents" in organization
            ? (organization.parents as ParentLimitedResponse[])
            : []
        }
        tutors={
          organization && "tutors" in organization
            ? (organization.tutors as TutorPublicResponse[])
            : []
        }
        admins={
          organization && "admins" in organization
            ? (organization.admins as AccountLimitedResponse[])
            : []
        }
        // New task closes itself; a create reloads through refreshTasks.
        onClose={() => undefined}
        refreshTasks={tasksCreated}
      />
    </Dialog>
  );

  useEffect(() => {
    if (error) {
      Notifications.error(error.message || "An unexpected error occurred.");
    }
  }, [error]);

  if (embedded) {
    // The dashboard's Tasks view, laid out like its Communication and
    // Tutoring Hours views: due dates in the header row, dropdowns under it
    // with Create Task at their right, and a table in the card.
    const filterLabel = "mr-2 text-sm font-medium whitespace-nowrap";
    const day = (iso: string | null) =>
      iso ? moment(iso).format("YYYY-MM-DD") : "";
    const truncateText = (text: string, maxLength: number) =>
      text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

    /** People on a task as the Communication view's pills: two, then "+N more". */
    const renderPills = (users: TaskRecipientResponse[]) => {
      const people = pillPeople(users);
      const shown = people.slice(0, 2);
      return (
        <div className="flex min-w-0 flex-wrap gap-1">
          {shown.map((p) => (
            <PersonPill
              key={p.key}
              label={p.name}
              color={p.color}
              onClick={p.open}
            />
          ))}
          {people.length > shown.length && (
            <PersonPill label={`+${people.length - shown.length} more`} />
          )}
        </div>
      );
    };

    const dueDates = (
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium whitespace-nowrap">
          Due From:
        </label>
        <input
          type="date"
          aria-label="Due from"
          className={dateInputClasses}
          value={day(filters.dateRange.start)}
          onChange={(e) =>
            setFilters({
              ...filters,
              dateRange: {
                ...filters.dateRange,
                start: e.target.value
                  ? moment(e.target.value).startOf("day").toISOString()
                  : null,
              },
            })
          }
        />
        <label className="text-sm font-medium whitespace-nowrap">To:</label>
        <input
          type="date"
          aria-label="Due to"
          className={dateInputClasses}
          value={day(filters.dateRange.end)}
          onChange={(e) =>
            setFilters({
              ...filters,
              dateRange: {
                ...filters.dateRange,
                end: e.target.value
                  ? moment(e.target.value).endOf("day").toISOString()
                  : null,
              },
            })
          }
        />
      </div>
    );

    const dropdowns = (
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <label className={filterLabel}>Search:</label>
          <input
            type="text"
            className={dateInputClasses}
            placeholder="Search by key word..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className={filterLabel}>View:</label>
          <div style={{ minWidth: 160 }}>
            <Select
              // Its menu fits the longer view names (index.css).
              classNamePrefix="tasks-view-filter"
              options={viewOptions}
              value={{ value: filters.view, label: viewLabel(filters.view) }}
              onChange={(option) =>
                option &&
                setFilters({
                  ...filters,
                  view: option.value as TaskFilters["view"],
                  selectedUsers: [],
                })
              }
            />
          </div>
        </div>
        {!["all_tasks", "my_tasks"].includes(filters.view) && (
          <div className="flex items-center gap-2">
            <label className={filterLabel}>{userFilterLabel}:</label>
            <div style={{ minWidth: 160 }}>
              <Select
                isMulti
                options={userFilterOptions}
                value={filters.selectedUsers}
                onChange={(options) =>
                  setFilters({
                    ...filters,
                    selectedUsers: options as TaskFilters["selectedUsers"],
                  })
                }
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className={filterLabel}>Status:</label>
          <div style={{ minWidth: 160 }}>
            <Select
              options={statusFilterOptions}
              value={statusFilterOptions.find(
                (o) => o.value === filters.status
              )}
              onChange={(option) =>
                option &&
                setFilters({
                  ...filters,
                  status: option.value as TaskFilters["status"],
                })
              }
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className={filterLabel}>Due Date:</label>
          <div style={{ minWidth: 160 }}>
            <Select
              options={sortOptions}
              value={sortOptions.find((o) => o.value === filters.sortBy)}
              onChange={(option) =>
                option &&
                setFilters({
                  ...filters,
                  sortBy: option.value as TaskFilters["sortBy"],
                })
              }
            />
          </div>
        </div>
      </div>
    );

    return (
      <TasksFrame ref={dialogRef} embedded>
        {slots.dates && createPortal(dueDates, slots.dates)}
        {slots.filters &&
          createPortal(
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              {dropdowns}
              <div className="md:ml-auto flex-none">
                {renderCreateTask(
                  <Button color={ButtonColor.GREEN}>Create Task</Button>
                )}
              </div>
            </div>,
            slots.filters
          )}
        {!loaded ? (
          <div>Loading...</div>
        ) : filteredAndSortedTasks.length === 0 ? (
          <div>No tasks</div>
        ) : (
          // Fixed layout: the columns keep their shares, so a long name ends in
          // "…" inside its pill instead of widening Assigned to or About.
          <table className="w-full table-fixed bg-white border-collapse">
            {/* Above the rows' status menus and pills as they scroll under
                it; the open status menu is portaled above everything. */}
            <thead className="sticky top-0 z-20 bg-white shadow-sm">
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-2 text-sm font-bold w-[12%]">Due</th>
                <th className="text-left p-2 text-sm font-bold w-[16%]">
                  Task
                </th>
                <th className="text-left p-2 text-sm font-bold w-[20%]">
                  Description
                </th>
                <th className="text-left p-2 text-sm font-bold w-[15%]">
                  Assigned to
                </th>
                <th className="text-left p-2 text-sm font-bold w-[15%]">
                  About
                </th>
                <th className="text-center p-2 text-sm font-bold w-[17%]">
                  Status
                </th>
                <th className="text-center p-2 text-sm font-bold w-[5%]">
                  Details
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedTasks.map((task) => {
                const isExpanded = expandedTasks.has(task.id);
                return (
                  <Fragment key={task.id}>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-2 text-[13px]">
                        {moment(task.due_date).format("MMM Do, YYYY")}
                      </td>
                      <td className="p-2 text-[13px]">
                        {truncateText(task.name, 40)}
                        {automationOfTask(task) && (
                          <span className="mt-1 block">
                            <AutomaticChip
                              automation={automationOfTask(task)!}
                            />
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-[13px]">
                        {truncateText(task.content, 75)}
                      </td>
                      <td className="p-2">
                        {renderPills(task.assigned_users)}
                      </td>
                      <td className="p-2">{renderPills(task.tagged_users)}</td>
                      <td className="p-2">
                        <div className="flex justify-center">
                          {renderStatusSelect(task, "8rem")}
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          aria-label="Details"
                          aria-expanded={isExpanded}
                          onClick={() => toggleTaskExpansion(task.id)}
                          className="p-1 hover:bg-gray-100 rounded-full transition-transform"
                          style={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                          }}
                        >
                          <ChevronDownIcon width="20" height="20" />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-200">
                        <td colSpan={7} className="p-4">
                          {task.id in taskUpdates ? (
                            renderEditForm(task)
                          ) : (
                            // The row's columns show who it's assigned to and
                            // about, so the details don't repeat them.
                            <div className="space-y-4">
                              <div>{renderDescription(task)}</div>
                              <div className="flex justify-end gap-2">
                                {renderEditButtons(task)}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        <NextUp
          key={nextUp[0]?.id}
          orgId={currently_selected_organization}
          items={nextUp}
          onClose={() => setNextUp([])}
        />
      </TasksFrame>
    );
  }

  return (
    <TasksFrame ref={dialogRef} embedded={embedded}>
      <TasksWaiting
        orgId={currently_selected_organization}
        tutor={currently_selected_role === Auth0AccountRole.ORG_TUTOR}
      />
      <div>
        <div className="task-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                className="input w-full"
                placeholder="Search by key word..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
            {currently_selected_role &&
              [Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
                currently_selected_role
              ) && (
                <div className="filter-group">
                  <label className="mb-2">View</label>
                  <Select
                    value={{
                      value: filters.view,
                      label: viewLabel(filters.view),
                    }}
                    onChange={(option) => {
                      if (option) {
                        setFilters({
                          ...filters,
                          view: option.value as TaskFilters["view"],
                          selectedUsers: [], // Reset selected users when view changes
                        });
                      }
                    }}
                    options={viewOptions}
                  />
                </div>
              )}
            {!["all_tasks", "my_tasks"].includes(filters.view) &&
              currently_selected_role &&
              [Auth0AccountRole.ORG_ADMIN, Auth0AccountRole.ORG_TUTOR].includes(
                currently_selected_role
              ) && (
                <div className="filter-group">
                  <label className="mb-2">{userFilterLabel}</label>
                  <Select
                    isMulti
                    value={filters.selectedUsers}
                    onChange={(options) => {
                      setFilters({
                        ...filters,
                        selectedUsers: options as {
                          value: number | string;
                          label: string;
                        }[],
                      });
                    }}
                    options={userFilterOptions}
                  />
                </div>
              )}
          </div>
          <div className="filters-row">
            <div className="filter-group">
              <label>Due Date</label>
              <Select
                value={sortOptions.find((o) => o.value === filters.sortBy)}
                onChange={(option) => {
                  if (option) {
                    setFilters({
                      ...filters,
                      sortBy: option.value as TaskFilters["sortBy"],
                    });
                  }
                }}
                options={sortOptions}
              />
            </div>
            <div className="filter-group">
              <label>Status</label>
              <Select
                value={{
                  value: filters.status,
                  label:
                    filters.status === "ALL"
                      ? "All"
                      : filters.status.charAt(0).toUpperCase() +
                        filters.status.slice(1).toLowerCase(),
                }}
                onChange={(option) => {
                  if (option) {
                    setFilters({
                      ...filters,
                      status: option.value as TaskStatus | "ALL",
                    });
                  }
                }}
                options={statusFilterOptions}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    pointerEvents: "auto",
                  }),
                  option: (base) => ({
                    ...base,
                    cursor: "pointer",
                  }),
                }}
              />
            </div>
            <div className="filter-group">
              <label>Start Date</label>
              <DatePicker
                oneTap
                value={
                  filters.dateRange.start
                    ? new Date(filters.dateRange.start)
                    : null
                }
                size="md"
                format="yyyy-MM-dd"
                placement="bottomStart"
                onChange={(val) => {
                  if (!val || isNaN(new Date(val).getTime())) {
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: null },
                    });
                    return;
                  }
                  try {
                    const localStart = new Date(val);
                    localStart.setHours(0, 0, 0, 0);
                    if (!isNaN(localStart.getTime())) {
                      setFilters({
                        ...filters,
                        dateRange: {
                          ...filters.dateRange,
                          start: localStart.toISOString(),
                        },
                      });
                    }
                  } catch (e) {
                    console.error("Invalid date:", e);
                  }
                }}
                container={popupContainer}
                cleanable
                block
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <DatePicker
                oneTap
                value={
                  filters.dateRange.end ? new Date(filters.dateRange.end) : null
                }
                size="md"
                format="yyyy-MM-dd"
                placement="bottomStart"
                onChange={(val) => {
                  if (!val || isNaN(new Date(val).getTime())) {
                    setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: null },
                    });
                    return;
                  }
                  try {
                    const localEnd = new Date(val);
                    localEnd.setHours(23, 59, 59, 999);
                    if (!isNaN(localEnd.getTime())) {
                      setFilters({
                        ...filters,
                        dateRange: {
                          ...filters.dateRange,
                          end: localEnd.toISOString(),
                        },
                      });
                    }
                  } catch (e) {
                    console.error("Invalid date:", e);
                  }
                }}
                container={popupContainer}
                cleanable
                block
              />
            </div>
          </div>
        </div>
      </div>

      <div className="task-list">
        {filteredAndSortedTasks.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-2xl mt-28">
            <div>No tasks! Click the button below to create one</div>
            <div className="mt-8">
              {renderCreateTask(
                <Button
                  fill={ButtonFill.HOLLOW}
                  color={ButtonColor.GREEN}
                  size={ButtonSize.LARGE}
                >
                  Create Task +
                </Button>
              )}
            </div>
          </div>
        ) : (
          filteredAndSortedTasks.map((task) => (
            <div
              key={task.id}
              className={`task-item border-l-4 ${
                task.status === TaskStatus.COMPLETE
                  ? "border-l-[#4adc8c]"
                  : task.status === TaskStatus.PENDING
                  ? "border-l-[#93c5fd]"
                  : task.status === TaskStatus.CANCELLED
                  ? "border-l-[#4b5563]"
                  : ""
              }`}
            >
              <div className="task-header">
                <div className="task-preview">
                  {
                    <>
                      <h3>
                        {task.name}
                        <span className="text-sm text-gray-400 ml-2 italic">
                          - due: {new Date(task.due_date).toLocaleDateString()}
                        </span>
                        {automationOfTask(task) && (
                          <span className="ml-2 align-middle">
                            <AutomaticChip
                              automation={automationOfTask(task)!}
                            />
                          </span>
                        )}
                      </h3>
                      {!expandedTasks.has(task.id) && (
                        <p>
                          {task.content.length > 125
                            ? `${task.content.substring(0, 125)}...`
                            : task.content}
                        </p>
                      )}
                    </>
                  }
                </div>
                <div className="task-actions">{renderStatusSelect(task)}</div>
              </div>
              {expandedTasks.has(task.id) && (
                <div className="task-details">
                  {task.id in taskUpdates ? (
                    renderEditForm(task)
                  ) : (
                    <>
                      {renderUserList("Assigned Users", task)}
                      {renderUserList("Tagged Users", task)}
                    </>
                  )}
                </div>
              )}
              <div className="bottom-actions mt-8">
                {expandedTasks.has(task.id) &&
                  !(task.id in taskUpdates) &&
                  renderEditButtons(task)}
                {!(expandedTasks.has(task.id) && taskUpdates[task.id]) && (
                  <button
                    className="expand-button"
                    onClick={() => toggleTaskExpansion(task.id)}
                  >
                    {expandedTasks.has(task.id) ? "−" : "+"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {filteredAndSortedTasks.length !== 0 && (
        <div className="bottom-task-navbar ">
          {renderCreateTask(
            <Button fill={ButtonFill.HOLLOW} color={ButtonColor.GREEN}>
              Create Task +
            </Button>
          )}
        </div>
      )}
      <NextUp
        key={nextUp[0]?.id}
        orgId={currently_selected_organization}
        items={nextUp}
        onClose={() => setNextUp([])}
      />
    </TasksFrame>
  );
}

export default ManageTasksDialog;
